import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { completeSetup, readSetup, setupHash, type SetupView } from '../api/athlete-sign-in.js';
import { ApiError } from '../api/client.js';
import { OneTimePassword } from '../components/one-time-password.js';
import { WaitNote } from '../components/wait-note.js';
import { BusyLabel } from '../components/busy-label.js';

/**
 * /athlete-sign-in?token=… — where the setup email's link lands (*Athlete
 * Portal* canvas: "Parent · choose Noa's username", and row 5's "agree again").
 *
 * Signed in, as every route is, and the API answers only the parent who asked
 * for the link (ruled 16 October). The words shown are the API's, sent back
 * unchanged, so the consent kept is exactly what was on the screen.
 *
 * The first time, the parent chooses the username. Turning back on, it is
 * shown and kept. Either way the button is slow: the account is made, or
 * switched back on and read back, before this answers.
 *
 * THE ONE-TIME PASSWORD STAYS HERE (canvas row F, approved 26 October). Once
 * activated, this page shows the username and the password, and moves on only
 * when the parent taps Done. One was missed on 26 October: the child's page
 * showed it inside a closed row and forgot it. Done still carries it to
 * the child's page, which opens that row on arrival (child-sign-in.tsx).
 */

type Load =
  | { status: 'loading' }
  | { status: 'dead' }
  | { status: 'failed' }
  | { status: 'ready'; hash: string; token: string; setup: SetupView };

export function AthleteSignInSetupRoute() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [username, setUsername] = useState('');
  const [usernameProblem, setUsernameProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [made, setMade] = useState<{ client_id: string; username: string; one_time_password: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!/^[A-Za-z0-9_-]{20,}$/.test(token)) {
      setLoad({ status: 'dead' });
      return;
    }
    void (async () => {
      try {
        const hash = await setupHash(token);
        const setup = await readSetup(getToken, hash);
        if (cancelled) return;
        setUsername((current) => current || setup.suggested_username);
        setLoad({ status: 'ready', hash, token, setup });
      } catch (cause) {
        if (cancelled) return;
        setLoad({ status: cause instanceof ApiError && cause.code === 'link_dead' ? 'dead' : 'failed' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, getToken, nonce]);

  if (load.status === 'loading') {
    return <WaitNote>Checking…</WaitNote>;
  }

  if (load.status === 'dead') {
    return (
      <section className="panel">
        <h2>This link no longer works</h2>
        <p className="statement">
          It was used, replaced by a newer one, or is more than 24 hours old. Ask for a new one from your child's page.
        </p>
        <Link className="primary" to="/account">
          Back to your account
        </Link>
      </section>
    );
  }

  if (load.status === 'failed') {
    return (
      <section className="panel">
        <p className="empty">That did not load.</p>
        <button type="button" className="secondary" onClick={() => setNonce((n) => n + 1)}>
          Try again
        </button>
      </section>
    );
  }

  const { setup, hash } = load;
  const name = setup.first_name;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (working || load.status !== 'ready') return;
    setWorking(true);
    setProblem(null);
    setUsernameProblem(null);
    try {
      const done = await completeSetup(getToken, hash, {
        token: load.token,
        username: setup.again ? null : username,
        words: setup.words,
      });
      // The one-time password stays on this page until Done, and is never
      // put in storage or the address.
      const password = done.one_time_password;
      if (password) setMade({ ...done, one_time_password: password });
      else navigate(`/account/people/${done.client_id}`);
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : '';
      if (code === 'username_taken') setUsernameProblem('That username is taken. Try another.');
      else if (code === 'username_invalid') setUsernameProblem('3 to 32 letters, numbers and single hyphens.');
      else if (code === 'link_dead') setLoad({ status: 'dead' });
      else if (code === 'words_changed') {
        setProblem('The wording has changed. Read it again.');
        setNonce((n) => n + 1);
      } else setProblem('That did not work, and nothing changed. Try again.');
    } finally {
      setWorking(false);
    }
  }

  if (made) {
    return (
      <>
        <div className="page-head">
          <div>
            <h2>{name}'s sign-in</h2>
            <span className="detail">
              {setup.first_name} {setup.last_name} · Born {setup.birth_year}
            </span>
          </div>
        </div>

        <div className="fact">
          <span className="label">Username</span>
          <span className="value">{made.username}</span>
        </div>

        <span className="section-label">One-time password</span>
        <OneTimePassword value={made.one_time_password} />
        <p className="statement">Give {name} this. It is shown once.</p>

        <button
          type="button"
          className="primary"
          onClick={() =>
            navigate(`/account/people/${made.client_id}`, {
              state: { oneTimePassword: made.one_time_password },
            })
          }
        >
          Done
        </button>
      </>
    );
  }

  return (
    <form onSubmit={(event) => void submit(event)}>
      <div className="page-head">
        <div>
          <h2>{name}'s sign-in</h2>
          <span className="detail">
            {setup.first_name} {setup.last_name} · Born {setup.birth_year}
          </span>
        </div>
      </div>

      <p className="statement">{setup.words.intro}</p>

      {setup.again ? (
        <div className="fact">
          <span className="label">Username</span>
          <span className="value">{setup.username}</span>
        </div>
      ) : (
        <div className="field" data-invalid={usernameProblem ? '' : undefined}>
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-describedby="username-hint"
          />
          <span className="hint" id="username-hint">
            {usernameProblem ?? (
              <>
                Letters, numbers and hyphens. <b>It can't be changed later.</b>
              </>
            )}
          </span>
        </div>
      )}

      <span className="section-label">{setup.again ? 'Password' : 'One-time password'}</span>
      <p className="statement">{setup.words.password}</p>

      <span className="section-label">What you're agreeing to</span>
      <p className="statement">{setup.words.agreement}</p>

      {problem ? (
        <p className="form-note" role="alert">
          {problem}
        </p>
      ) : null}

      <button type="submit" className="primary" disabled={working} aria-busy={working}>
        <BusyLabel busy={working} working={setup.again ? 'Reactivating… about 15 seconds' : 'Activating…'}>
          {setup.again ? `Reactivate ${name}'s access` : `Activate ${name}'s access`}
        </BusyLabel>
      </button>
    </form>
  );
}
